/**
 * Phases du Hero de la landing (admin, apps/app — migration 0512).
 *
 * `landing_hero_phases` est postérieure aux types générés → cast léger `heroDb`
 * (même pattern que `faq.ts`). Écriture directe PostgREST sous RLS admin ; les
 * fonds sont déposés dans le bucket public `hero-media` (écriture admin).
 * Validation : `heroPhaseInputSchema` (miroir des CHECK SQL).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_HERO_PHASES,
  HERO_MEDIA_BUCKET,
  heroMediaFileIssue,
  heroPhaseInputSchema,
  swapHeroPhaseOrder,
  type HeroPhase,
  type HeroPhaseInput,
} from '@jeraisonne/db/landing-hero';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const heroDb = supabase as unknown as { from: (table: string) => any };

const TABLE = 'landing_hero_phases';
const COLUMNS =
  'id, display_order, is_published, eyebrow, title, title_highlight, description, bullets, caption_title, caption_subtitle, media_type, media_url, poster_url, media_position, device, duration_ms';

export const HERO_PHASES_KEY = ['admin-landing-hero-phases'] as const;

/** Première erreur Zod → message lisible pour l'admin. */
export function heroInputError(input: HeroPhaseInput): string | null {
  const parsed = heroPhaseInputSchema.safeParse(input);
  if (parsed.success) return null;
  const issue = parsed.error.issues[0]!;
  const field = String(issue.path[0] ?? '');
  const labels: Record<string, string> = {
    eyebrow: 'Surtitre',
    title: 'Titre',
    title_highlight: 'Titre en couleur',
    description: 'Description',
    bullets: 'Puces',
    caption_title: 'Légende',
    caption_subtitle: 'Sous-légende',
    media_url: 'Image ou vidéo',
    poster_url: 'Image d’attente',
    duration_ms: 'Durée',
  };
  return `${labels[field] ?? field} : ${issue.message}`;
}

function parseInput(input: HeroPhaseInput) {
  const parsed = heroPhaseInputSchema.safeParse(input);
  if (!parsed.success) throw new Error(heroInputError(input) ?? 'Phase invalide.');
  return parsed.data;
}

export function useAdminHeroPhases() {
  return useQuery({
    queryKey: HERO_PHASES_KEY,
    queryFn: async (): Promise<HeroPhase[]> => {
      const { data, error } = await heroDb
        .from(TABLE)
        .select(COLUMNS)
        .order('display_order', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as HeroPhase[];
    },
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: HERO_PHASES_KEY });
}

export function useCreateHeroPhase() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: HeroPhaseInput) => {
      const { error } = await heroDb.from(TABLE).insert(parseInput(input));
      if (error) throw new Error(error.message);
    },
    onSettled: invalidate,
  });
}

export function useUpdateHeroPhase() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: HeroPhaseInput }) => {
      const { error } = await heroDb.from(TABLE).update(parseInput(input)).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: invalidate,
  });
}

export function useDeleteHeroPhase() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await heroDb.from(TABLE).delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: invalidate,
  });
}

/** Monte (-1) ou descend (+1) une phase : échange d'ordre avec sa voisine. */
export function useMoveHeroPhase() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      phases,
      id,
      delta,
    }: {
      phases: HeroPhase[];
      id: string;
      delta: -1 | 1;
    }) => {
      const updates = swapHeroPhaseOrder(phases, id, delta);
      if (!updates) return;
      for (const u of updates) {
        const { error } = await heroDb
          .from(TABLE)
          .update({ display_order: u.display_order })
          .eq('id', u.id);
        if (error) throw new Error(error.message);
      }
    },
    onSettled: invalidate,
  });
}

/** Remet les 5 phases par défaut (liste vide seulement — proposé par l'UI). */
export function useSeedDefaultHeroPhases() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async () => {
      const rows = DEFAULT_HERO_PHASES.map(({ id: _id, ...rest }) => rest);
      const { error } = await heroDb.from(TABLE).insert(rows);
      if (error) throw new Error(error.message);
    },
    onSettled: invalidate,
  });
}

/** Extension de fichier sûre, déduite du type MIME (jamais du nom fourni). */
export function heroMediaExtension(mime: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
  };
  return map[mime] ?? 'bin';
}

/** Dépose une image ou une vidéo de fond → URL publique. */
export async function uploadHeroMedia(file: File): Promise<string> {
  const issue = heroMediaFileIssue(file);
  if (issue) throw new Error(issue);
  const path = `${crypto.randomUUID()}.${heroMediaExtension(file.type)}`;
  const { error } = await supabase.storage
    .from(HERO_MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type, cacheControl: '31536000', upsert: false });
  if (error) throw new Error(error.message);
  return supabase.storage.from(HERO_MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}
